import "moment/locale/pt-br";
import "react-datetime/css/react-datetime.css";
import React, { useLayoutEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    Flex,
    FormControl,
    FormErrorMessage,
    FormLabel,
    Input,
    useColorModeValue,
    useDisclosure,
} from "@chakra-ui/react";
import { STATUS_LIST, DOCUMENT_TYPE_LIST } from "services/drvService";
import { FilterContainer } from "components/Tables/FilterContainer";
import SearchSelect from "components/SearchSelect/SearchSelect";
import DateTime from "react-datetime";
import { LOCALE_PT_BR } from "constants/date-time";
import { plateMask, validateVehiclePlate } from "utils/forms-utils";
import { parseDateBrToISO } from "utils/utils";

export const DrvFilter = (props) => {
    const { t } = useTranslation('common');
    const { onFilter, filterSnapshot } = props;
    const { isOpen, onToggle } = useDisclosure();

    const [plate, setPlate] = useState("");
    const [status, setStatus] = useState("");
    const [documentType, setDocumentType] = useState("");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const placeholderColor = useColorModeValue("gray.300", "gray.400");

    const isStartDateInvalid = startDate ? startDate > new Date() : false;
    const isEndDateInvalid = startDate && endDate ? endDate <= startDate : false;
    const [plateError, setPlateError] = useState(null);

    const handlePlateChange = (e) => {
        const maskedPlate = plateMask(e.target.value);
        setPlate(maskedPlate);
        if (plateError) setPlateError(null);
    };

    const onFilterHandler = () => {
        onFilter({
            placa: plate,
            status: status,
            tipo: documentType,
            data_inicial: startDate ? new Date(startDate).toLocaleDateString(LOCALE_PT_BR) : "",
            data_final: endDate ? new Date(endDate).toLocaleDateString(LOCALE_PT_BR) : ""
        });
    }

    const onClearHandler = () => {
        setPlate("");
        setStatus("");
        setDocumentType("");
        setStartDate("");
        setEndDate("");
        setPlateError(null);

        onFilter({
            placa: null,
            status: null,
            tipo: null,
            data_inicial: null,
            data_final: null,
        });
    }

    useLayoutEffect(() => {
        if (filterSnapshot == null) {
            setPlate("");
            setStatus("");
            setDocumentType("");
            setStartDate("");
            setEndDate("");
            return;
        }
        setPlate(filterSnapshot.placa ? plateMask(String(filterSnapshot.placa)) : "");
        setStatus(filterSnapshot.status != null && filterSnapshot.status !== "" ? String(filterSnapshot.status) : "");
        setDocumentType(filterSnapshot.tipo != null && filterSnapshot.tipo !== "" ? String(filterSnapshot.tipo) : "");
        if (filterSnapshot.data_inicial) {
            const d = parseDateBrToISO(filterSnapshot.data_inicial);
            setStartDate(d && !isNaN(d.getTime()) ? d : "");
        } else {
            setStartDate("");
        }
        if (filterSnapshot.data_final) {
            const d = parseDateBrToISO(filterSnapshot.data_final);
            setEndDate(d && !isNaN(d.getTime()) ? d : "");
        } else {
            setEndDate("");
        }
    }, [filterSnapshot]);

    const handleToggle = () => {
        if (isOpen) {
            setPlateError(null);
        }
        onToggle();
    };

    return (
        <FilterContainer
            w='125px'
            fontSize='sm'
            fontWeight='bold'
            variant="light"
            isOpen={isOpen}
            onToggle={handleToggle}
            onFilter={onFilterHandler}
            onClear={onClearHandler}
        >
            <FormControl isInvalid={!!plateError}>
                <FormLabel>
                    {t("digital-collections.drv.filters.fields.plate")}
                </FormLabel>
                <Input
                    fontSize="sm"
                    placeholder={t("digital-collections.drv.filters.placeholders.plate")}
                    _placeholder={{ color: placeholderColor, fontSize: "sm" }}
                    value={plate}
                    onBlur={() => {
                        const err = validateVehiclePlate(plate, t("digital-collections.drv.filters.fields.plate"));
                        setPlateError(err);
                    }}
                    onChange={handlePlateChange}
                />
                <FormErrorMessage>
                    {plateError && t(plateError)}
                </FormErrorMessage>
            </FormControl>

            <FormControl>
                <FormLabel>
                    {t("digital-collections.drv.filters.fields.status")}
                </FormLabel>
                <SearchSelect
                    items={STATUS_LIST}
                    name="status"
                    valueField="id"
                    displayField="status"
                    value={status}
                    onChange={e => setStatus(e.currentTarget.value)}
                    placeholder={t("digital-collections.drv.filters.placeholders.status")}
                />
            </FormControl>

            <FormControl>
                <FormLabel>
                    {t("digital-collections.drv.filters.fields.type")}
                </FormLabel>
                <SearchSelect
                    items={DOCUMENT_TYPE_LIST}
                    name="type"
                    valueField="id"
                    displayField="type"
                    value={documentType}
                    onChange={e => setDocumentType(e.currentTarget.value)}
                    placeholder={t("digital-collections.drv.filters.placeholders.type")}
                />
            </FormControl>

            <Flex w="100%" gap="4" justify="space-between">
                <FormControl w="230px" isInvalid={isStartDateInvalid}>
                    <FormLabel>
                        {t("system.labels.start-date")}
                    </FormLabel>
                    <DateTime
                        locale={LOCALE_PT_BR}
                        closeOnSelect
                        timeFormat={false}
                        inputProps={startDate ? undefined : { value: "", placeholder: t("system.placeholders.date") }}
                        renderInput={(props) => (
                            <Input
                                {...props}
                                fontSize="sm"
                                _placeholder={{ color: placeholderColor, fontSize: "sm" }}
                            />
                        )}
                        onChange={date => setStartDate(date)} />
                    <FormErrorMessage>
                        {t("system.messages.errors.invalid-start-date")}
                    </FormErrorMessage>
                </FormControl>

                <FormControl w="230px" isInvalid={isEndDateInvalid}>
                    <FormLabel>
                        {t("system.labels.end-date")}
                    </FormLabel>
                    <DateTime
                        locale={LOCALE_PT_BR}
                        closeOnSelect
                        timeFormat={false}
                        inputProps={endDate ? undefined : { value: "", placeholder: t("system.placeholders.date") }}
                        renderInput={(props) => (
                            <Input
                                {...props}
                                fontSize="sm"
                                _placeholder={{ color: placeholderColor, fontSize: "sm" }}
                            />
                        )}
                        onChange={date => setEndDate(date)} />
                    <FormErrorMessage>
                        {t("system.messages.errors.invalid-end-date")}
                    </FormErrorMessage>
                </FormControl>
            </Flex>
        </FilterContainer>
    );

};